import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  User,
  signInWithCustomToken,
  Auth,
} from "firebase/auth";
import type { ReactNode } from "react";
import posthog from "posthog-js";

export interface FirebaseContextType {
  app: FirebaseApp;
  auth: Auth;
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  signInWithCustomToken: typeof signInWithCustomToken;
}

export const FirebaseContext = createContext<FirebaseContextType | undefined>(
  undefined,
);

export const FirebaseProvider = ({
  children,
  app,
}: {
  children: ReactNode;
  app: FirebaseApp;
}) => {
  const auth = getAuth(app);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const logoutInFlightRef = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      setUser(fbUser);
      setLoading(false);
    });
    return unsubscribe;
  }, [auth]);

  // Memoized so UserContext's 401 effect doesn't re-fire on every render.
  const logout = useCallback(async () => {
    if (logoutInFlightRef.current) return;
    logoutInFlightRef.current = true;
    try {
      await signOut(auth);
      // Only reset PostHog when there was an identified user — calling
      // reset() on an anonymous session generates a fresh distinct id and
      // orphans prior anonymous events.
      if (posthog.get_property("$user_id")) {
        posthog.reset();
      }
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      logoutInFlightRef.current = false;
    }
  }, [auth]);

  return (
    <FirebaseContext.Provider
      value={{
        app,
        auth,
        user,
        loading,
        logout,
        signInWithCustomToken,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = (): FirebaseContextType => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error("useFirebase must be used within a FirebaseProvider");
  }
  return context;
};
